import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnInit,
  Output,
  Provider,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { AppConfig } from '@luggagehero/shared/app-settings/data-access';
import { IntlTelInputData, IPhoneCountry } from '@luggagehero/shared/interfaces';
import { SharedIpLookupService } from '@luggagehero/shared/services/ip-lookup';
import { SharedTranslateService } from '@luggagehero/shared/services/translation';
import { SharedDirectiveAutofocusDirective, SharedIntlTelInputDirective } from '@luggagehero/shared/ui-directives';
import { placeholderNumberType } from 'intl-tel-input';

const noop = () => {
  /**/
};

export enum PhoneNumberType {
  'FIXED_LINE' = 0,
  'MOBILE' = 1,
  // In some regions (e.g. the USA), it is impossible to distinguish between
  // fixed-line and mobile numbers by looking at the phone number itself.
  'FIXED_LINE_OR_MOBILE' = 2,
  // Freephone lines
  'TOLL_FREE' = 3,
  'PREMIUM_RATE' = 4,
  // The cost of this call is shared between the caller and the recipient, and
  // is hence typically less than PREMIUM_RATE calls. See
  // http://en.wikipedia.org/wiki/Shared_Cost_Service for more information.
  'SHARED_COST' = 5,
  // Voice over IP numbers. This includes TSoIP (Telephony Service over IP).
  'VOIP' = 6,
  // A personal number is associated with a particular person, and may be routed
  // to either a MOBILE or FIXED_LINE number. Some more information can be found
  // here = http://en.wikipedia.org/wiki/Personal_Numbers
  'PERSONAL_NUMBER' = 7,
  'PAGER' = 8,
  // Used for 'Universal Access Numbers' or 'Company Numbers'. They may be
  // further routed to specific offices, but allow one number to be used for a
  // company.
  'UAN' = 9,
  // Used for 'Voice Mail Access Numbers'.
  'VOICEMAIL' = 10,
  // A phone number is of type UNKNOWN when it does not fit any of the known
  // patterns for a specific region.
  'UNKNOWN' = -1,
}

export interface AdvancedPhoneInputOptions extends intlTelInput.Options {
  allowDropdown?: boolean;
  autoHideDialCode?: boolean;
  autoPlaceholder?: 'polite' | 'aggressive' | 'off';
  customPlaceholder?: (selectedCountryPlaceholder: string, selectedCountryData: unknown) => string;
  dropdownContainer?: Node;
  excludeCountries?: string[];
  formatOnDisplay?: boolean;
  geoIpLookup?: ((callback: (countryCode: string) => void) => void) | undefined;
  hiddenInput?: string;
  initialCountry?: string;
  localizedCountries?: { [countryCode: string]: string };
  nationalMode?: boolean;
  onlyCountries?: string[];
  placeholderNumberType?: placeholderNumberType;
  preferredCountries?: string[];
  separateDialCode?: boolean;
  utilsScript?: string;
}

export const PHONE_INPUT_CONTROL_VALUE_ACCESSOR: Provider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => SharedFeaturePhoneInputComponent),
  multi: true,
};
export const PHONE_INPUT_VALIDATOR: Provider = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => SharedFeaturePhoneInputComponent),
  multi: true,
};

@Component({
  selector: 'lh-shared-feature-phone-input',
  imports: [SharedIntlTelInputDirective, FormsModule, ReactiveFormsModule, SharedDirectiveAutofocusDirective],
  templateUrl: './shared-feature-phone-input.component.html',
  styleUrl: './shared-feature-phone-input.component.scss',
  providers: [PHONE_INPUT_CONTROL_VALUE_ACCESSOR, PHONE_INPUT_VALIDATOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SharedFeaturePhoneInputComponent implements ControlValueAccessor, Validator, OnInit, AfterViewInit {
  private ipLookup = inject(SharedIpLookupService);
  private translate = inject(SharedTranslateService);
  private cd = inject(ChangeDetectorRef);
  @Input() public tabindex = 0;

  @ViewChild('intlTelInput') public telInput?: ElementRef<HTMLInputElement>;
  @ViewChildren(SharedIntlTelInputDirective) intlTelInputDirectives?: QueryList<SharedIntlTelInputDirective>;

  @Output() public selectedCountryChange = new EventEmitter<string>();
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() public focus = new EventEmitter<void>();
  @Input() public requestFocus = false;
  @Input() public readonly = false;

  /**
   * Forwarded onto the real focusable input.
   *
   * An attribute binding placed on this component's TAG lands on the host element, which is not
   * focusable and which assistive tech never reaches — so a consumer wiring up validation state
   * from outside produced markup that looked correct and announced nothing. These inputs are the
   * supported way in; both template branches apply them.
   */
  @Input() public ariaInvalid: boolean | null = null;
  @Input() public ariaDescribedBy: string | null = null;

  /**
   * The id of the real focusable input, so a consumer's `<label for>` can associate with it.
   *
   * Not named `id`: `[id]` on this component's tag is a host-element binding, and a host element
   * is not labelable — the `for` resolves to it and the association silently never forms, leaving
   * the field with no accessible name at all. Same trap as the aria inputs above, which is why it
   * is forwarded the same way rather than left to the host.
   */
  @Input() public inputId: string | null = null;

  public advancedOptions: AdvancedPhoneInputOptions = {
    geoIpLookup: (callback) => {
      this.ipLookup
        .locate()
        .then((res) => callback(res.country_code))
        .catch(() => callback(AppConfig.INITIAL_PHONE_COUNTRY_CODE));
    },
    initialCountry: 'auto', // AppConfig.INITIAL_PHONE_COUNTRY_CODE,
    preferredCountries: AppConfig.PREFERRED_PHONE_COUNTRIES,
    separateDialCode: true,
    utilsScript: 'assets/scripts/intl-tel-input/utils.js',
    customPlaceholder: () => this.translate.instant('PHONE_NUMBER'),
  };

  private _value?: string;
  private _placeholder?: string;
  private _selectedDialCode?: string;
  private _selectedCountry?: string;
  private _countries?: IPhoneCountry[];
  private _phoneNumber = '';
  private _advancedMode = false;
  private isFirstUpdate = true;
  private _pendingValue?: string;

  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: unknown) => void = noop;

  get value(): string | undefined {
    return this._value;
  }
  set value(value: string) {
    if (!value || value === this._value) {
      return;
    }
    this._value = value;

    if (value !== this.selectedDialCode) {
      this.onChangeCallback(value);
    }
  }

  get placeholder(): string | undefined {
    return this._placeholder;
  }
  @Input() set placeholder(value: string) {
    this._placeholder = value;
    this.cd.markForCheck();
  }

  get countries(): IPhoneCountry[] | undefined {
    return this._countries;
  }
  @Input() set countries(value: IPhoneCountry[]) {
    this._countries = value;
    this.cd.markForCheck();
  }

  get advancedMode(): boolean {
    return this._advancedMode;
  }
  @Input() set advancedMode(value: boolean) {
    this._advancedMode = value;
    this.cd.markForCheck();
  }

  get selectedCountry(): string | undefined {
    return this._selectedCountry;
  }
  @Input() set selectedCountry(value: string) {
    if (!value || value === this._value) {
      return;
    }
    if (this.advancedMode) {
      this._selectedCountry = value;
      this.selectedCountryChange.emit(this._selectedCountry);
    } else {
      // This will set the country code by looking it up from our list of countries
      this.selectedDialCode = this.countries?.find((c) => c.countryCode === value)?.dialCode;
    }
  }

  get selectedDialCode(): string | undefined {
    return this._selectedDialCode;
  }
  set selectedDialCode(value: string | undefined) {
    if (!value || value === this._value) {
      return;
    }
    this._selectedDialCode = value;
    this.updateValue();

    if (!this.advancedMode) {
      this._selectedCountry = this.countries?.find((c) => c.dialCode === value)?.countryCode;
      this.selectedCountryChange.emit(this._selectedCountry);
    }
  }

  get phoneNumber(): string {
    return this._phoneNumber;
  }
  set phoneNumber(value: string) {
    this._phoneNumber = value;
    this.updateValue();
  }

  ngOnInit() {
    if (this.advancedMode) {
      this.selectedDialCode = AppConfig.INITIAL_PHONE_DIAL_CODE;
      this.selectedCountry = AppConfig.INITIAL_PHONE_COUNTRY_CODE;
    } else if (this.countries) {
      this.selectedDialCode = this.countries[0].dialCode;
    }
  }

  ngAfterViewInit(): void {
    if (this._pendingValue !== undefined) {
      const directive = this.intlTelInputDirectives?.first;
      if (directive?.intlTelInput) {
        const pending = this._pendingValue;
        directive.intlTelInput.setNumber(pending);
        // setNumber fires a synchronous countrychange event that calls updateValue(),
        // which resets _phoneNumber to "" and _value to just the dial code. Read back
        // the national number from the DOM and restore the full E.164 value.
        this._phoneNumber = this.telInput?.nativeElement.value ?? '';
        this._value = pending;
        this.cd.markForCheck();
      }
      this._pendingValue = undefined;
    }
  }

  writeValue(value: string) {
    if (value !== undefined) {
      if (this.advancedMode) {
        // In advanced mode, update internal state without calling onChangeCallback.
        // Calling onChangeCallback from writeValue violates the ControlValueAccessor contract
        // and would incorrectly mark the form as dirty on initial load.
        this._value = value;
        const directive = this.intlTelInputDirectives?.first;
        if (directive?.intlTelInput) {
          directive.intlTelInput.setNumber(value || '');
          // setNumber fires a synchronous countrychange event that calls updateValue(),
          // which resets _phoneNumber to "" and _value to just the dial code. Read back
          // the national number from the DOM and restore the full E.164 value.
          this._phoneNumber = this.telInput?.nativeElement.value ?? '';
          this._value = value;
          this.cd.markForCheck();
        } else {
          this._pendingValue = value;
        }
      } else {
        this.value = value;
      }
    }
  }

  registerOnChange(fn: (_: unknown) => void) {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onTouchedCallback = fn;
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    if (!this._value || !this._value.trim()) {
      return null;
    }
    const directive = this.intlTelInputDirectives?.first;
    if (!directive) {
      // The intl-tel directive isn't attached yet (transient during view init/teardown). Treat as
      // "no opinion" rather than dereferencing `.first` and throwing mid-validation.
      return null;
    }
    const err = directive.getValidationError();
    if (err) {
      return { invalid: { reason: err, input: this.value } };
    }
    return null;
  }

  onIntlTelInputChange(value: IntlTelInputData) {
    if (value.countryData) {
      this.selectedCountry = value.countryData.iso2;
      this.selectedDialCode = `+${value.countryData.dialCode}`;
    }

    // Avoid setting dirty on initial setting of country
    if (value.phoneNumber || !this.isFirstUpdate) {
      this.isFirstUpdate = false;
      this.onChangeCallback(this.value);
    }
  }

  onInputBlur() {
    this.onTouchedCallback();
  }

  onInputFocus() {
    this.focus.emit();
  }

  setFocus() {
    if (!this.telInput) {
      return;
    }
    this.telInput.nativeElement.focus();
  }

  setSelected() {
    if (!this.telInput) {
      return;
    }
    this.telInput.nativeElement.select();
  }
  updateValue() {
    // Remove dial code from the phone number if it matches the dial code selected in the dropdown
    if (this.selectedDialCode && this.phoneNumber.startsWith(this.selectedDialCode)) {
      this.phoneNumber = this.phoneNumber.substring(this.selectedDialCode.length);
    }

    // Check if the user pasted or entered phone number with dial code icluded
    const phoneNumberIncludesDialCode = this.phoneNumber.match(/^\+|^(00)/) ? true : false;

    // Change 00 to + for dial codes included in the phone number itself
    if (phoneNumberIncludesDialCode && this.phoneNumber.startsWith('00')) {
      this.phoneNumber = this.phoneNumber.replace(/^(00)/, '+');
    }

    // Set value depending on whether dial code is already included in the phone number
    this.value = phoneNumberIncludesDialCode ? this.phoneNumber : `${this.selectedDialCode}${this.phoneNumber}`;
  }
}
